import {
  Grid,
  Button,
  ActionArea,
  Card,
  Separator,
} from '@design-systems/canvas-ui'
import Icon, { ArrowUpRightIcon } from '@design-systems/react-icons'
import type { ReactNode } from 'react'
import {
  getDomainLabel,
  getRefineSearchLink,
} from '../../utils/globalSearchHelpers'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client/react'
import {
  FEDERATED_ADD_RECENTLY_SEARCHED,
  RECENT_SEARCH_REFETCH_QUERIES,
} from 'graphql/queries/globalSearch/recentSearchesQueries'

type DomainSearchItem = {
  id: string
  domain: string
  description: string
  entityType: string
  score: number
}

type DomainGroupProps = {
  domainName: string
  content: DomainSearchItem[]
  getBoldText: (text: string) => ReactNode
  searchIndex: number
  startIndex: number
  setIndex: React.Dispatch<React.SetStateAction<number>>
  onSelectItem: (item: DomainSearchItem) => void
  setShowQuickSearchDropdown: (show: boolean) => void
  searchTerm: string
  icon: React.FunctionComponent<null> | null
  iconColor: string
}

export const DomainGroup = ({
  domainName,
  content,
  getBoldText,
  searchIndex,
  startIndex,
  setIndex,
  onSelectItem,
  setShowQuickSearchDropdown,
  searchTerm,
  icon,
  iconColor,
}: DomainGroupProps) => {
  const navigate = useNavigate()
  const mappedDomainName = getDomainLabel(domainName)
  const [addRecentlySearched] = useMutation(FEDERATED_ADD_RECENTLY_SEARCHED, {
    refetchQueries: RECENT_SEARCH_REFETCH_QUERIES,
    awaitRefetchQueries: true,
  })

  return (
    <>
      <Separator className="hc8-pv-2x" />
      <Grid.Container spacing="none">
        <Grid.Container spacing="none" align="center">
          <Grid.Item size={{ xs: true }}>
            <strong>{mappedDomainName}</strong>
          </Grid.Item>
          <Grid.Item>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                addRecentlySearched({ variables: { searchTerm } })
                navigate(getRefineSearchLink(domainName, searchTerm) ?? '#')
                setShowQuickSearchDropdown(false)
              }}
            >
              Refine search
              <Icon svg={ArrowUpRightIcon} className="hc8-clr-brand" />
            </Button>
          </Grid.Item>
        </Grid.Container>

        {content.map((item, itemIndex) => {
          const globalIndex = startIndex + itemIndex

          return (
            <Grid.Container
              spacing="none"
              key={`${item.id}`}
              onMouseEnter={() => setIndex(globalIndex)}
              onMouseLeave={() => setIndex(-1)}
            >
              <ActionArea corners="all" onClick={() => onSelectItem(item)}>
                <Card
                  className={
                    searchIndex === globalIndex
                      ? 'hc8-ph-2x hc8-pv-1x hc8-bg-subdued-active'
                      : 'hc8-ph-2x hc8-pv-1x'
                  }
                >
                  <Grid.Container align="center">
                    <Grid.Item>
                      <Card variant="border">
                        <Icon
                          svg={icon}
                          color={iconColor}
                          size={'xl'}
                          className={`hc8-ma-2x`}
                        />
                      </Card>
                    </Grid.Item>
                    <Grid.Item size={{ xs: true }}>
                      <p>{getBoldText(item.id)}</p>
                      <p>{getBoldText(item.description)}</p>
                    </Grid.Item>
                  </Grid.Container>
                </Card>
              </ActionArea>
            </Grid.Container>
          )
        })}
      </Grid.Container>
    </>
  )
}
